<script setup>
/**
 * MCP connectors — the remote tool servers this workspace lets Tola AI call.
 *
 * A connector is a standing outbound grant: once it is on and reachable, the
 * model can invoke whatever the remote server advertises. So the list leads
 * with the two facts that decide whether that grant is live — whether it is
 * enabled, and whether it authenticates — rather than burying them in an edit
 * form.
 *
 * Testing is the only way to see what a connector actually offers. The
 * credential is write-only, so a probe of a saved row runs server-side against
 * the stored secret and answers with what the remote server says about itself
 * and its tools; that answer is rendered under the row it belongs to.
 *
 * `insecure_endpoint` is a row that predates the templated-host check. It is
 * blocked from executing until re-saved, and it is called out in place because
 * nothing else on screen would explain why a connector never runs.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import McpServersAPI from 'dashboard/api/captain/mcpServers';

import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmDialog from 'dashboard/components-next/dialog/ConfirmDialog.vue';
import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsStatusChip from 'dashboard/components-next/Settings/SettingsStatusChip.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

import McpConnectorDialog from './McpConnectorDialog.vue';

const { t } = useI18n();

const connectors = ref([]);
const isLoading = ref(true);
const isSaving = ref(false);
const testingId = ref(null);
const dialogTestResult = ref(null);
// Probe results keyed by connector id, so a row keeps its own answer.
const rowTestResults = ref({});
const connectorDialog = ref(null);
const showDeleteConfirm = ref(false);
const pendingConnector = ref(null);

const isTestingDialog = computed(() => testingId.value === 'dialog');

const fetchConnectors = async () => {
  const { data } = await McpServersAPI.get();
  connectors.value = data?.payload || [];
};

// Every refusal here is one the server phrased — a validation on the endpoint,
// the per-account cap, a handshake that failed. Showing it verbatim is the
// point; this screen does not know which rule was broken.
const run = async (work, successMessage) => {
  try {
    const result = await work();
    if (successMessage) useAlert(successMessage);
    return result ?? true;
  } catch (error) {
    useAlert(String(parseAPIErrorResponse(error)));
    return false;
  }
};

onMounted(async () => {
  await run(fetchConnectors);
  isLoading.value = false;
});

const openDialog = (connector = null) => {
  dialogTestResult.value = null;
  connectorDialog.value?.open(connector);
};

const onSubmit = async ({ id, payload }) => {
  isSaving.value = true;
  const saved = await run(
    () =>
      id ? McpServersAPI.update(id, payload) : McpServersAPI.create(payload),
    t('MCP_CONNECTORS.FORM.SAVED')
  );
  isSaving.value = false;
  if (!saved) return;

  await run(fetchConnectors);
  connectorDialog.value?.close();
};

// The response carries only what the remote server reports about itself, never
// the credential that got us in.
const probe = async ({ id, payload }) => {
  try {
    const { data } = id
      ? await McpServersAPI.testSaved(id, payload)
      : await McpServersAPI.test(payload);
    return {
      ok: true,
      message: t('MCP_CONNECTORS.TEST.SUCCESS', {
        count: data.tools?.length || 0,
      }),
      protocolVersion: data.protocol_version,
      serverInfo: data.server_info,
      tools: data.tools || [],
    };
  } catch (error) {
    return { ok: false, message: String(parseAPIErrorResponse(error)) };
  }
};

const onTestFromDialog = async ({ id, payload }) => {
  testingId.value = 'dialog';
  dialogTestResult.value = await probe({ id, payload });
  testingId.value = null;
};

const onTestRow = async connector => {
  testingId.value = connector.id;
  rowTestResults.value = {
    ...rowTestResults.value,
    [connector.id]: await probe({ id: connector.id, payload: null }),
  };
  testingId.value = null;
};

// Only `enabled` is sent: the credential is write-only and omitting it is what
// keeps the stored one in place.
const onToggleEnabled = async connector => {
  const updated = await run(
    () => McpServersAPI.update(connector.id, { enabled: !connector.enabled }),
    connector.enabled
      ? t('MCP_CONNECTORS.LIST.DISABLED')
      : t('MCP_CONNECTORS.LIST.ENABLED')
  );
  if (updated) await run(fetchConnectors);
};

const confirmDelete = connector => {
  pendingConnector.value = connector;
  showDeleteConfirm.value = true;
};

const onDelete = async () => {
  const deleted = await run(
    () => McpServersAPI.delete(pendingConnector.value.id),
    t('MCP_CONNECTORS.LIST.DELETED')
  );
  showDeleteConfirm.value = false;
  pendingConnector.value = null;
  if (deleted) await run(fetchConnectors);
};
</script>

<template>
  <SettingsPageShell
    wide
    :title="t('MCP_CONNECTORS.HEADER.TITLE')"
    :description="t('MCP_CONNECTORS.HEADER.DESCRIPTION')"
  >
    <template #actions>
      <Button
        size="sm"
        icon="i-lucide-plus"
        :label="t('MCP_CONNECTORS.LIST.NEW')"
        data-testid="mcp-connector-new"
        @click="openDialog()"
      />
    </template>

    <p v-if="isLoading" class="text-sm text-n-slate-11">
      {{ t('MCP_CONNECTORS.LIST.LOADING') }}
    </p>

    <p
      v-else-if="!connectors.length"
      class="py-16 text-sm text-center text-n-slate-11"
      data-testid="mcp-connector-empty"
    >
      {{ t('MCP_CONNECTORS.LIST.EMPTY') }}
    </p>

    <ul
      v-else
      class="flex flex-col gap-2 m-0 list-none"
      data-testid="mcp-connector-list"
    >
      <li
        v-for="connector in connectors"
        :key="connector.id"
        class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
        :data-testid="`mcp-connector-row-${connector.id}`"
      >
        <div class="flex items-start gap-3 min-w-0">
          <div class="flex flex-col min-w-0 gap-1">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ connector.name }}
            </span>
            <span class="text-xs truncate text-n-slate-11">
              {{ connector.endpoint_url }}
            </span>
            <div class="flex flex-wrap items-center gap-2 mt-1">
              <SettingsStatusChip
                :label="connector.transport.toUpperCase()"
                tone="neutral"
              />
              <SettingsStatusChip
                :label="
                  connector.auth_configured
                    ? t('MCP_CONNECTORS.LIST.AUTHENTICATED')
                    : t('MCP_CONNECTORS.LIST.ANONYMOUS')
                "
                :tone="connector.auth_configured ? 'success' : 'neutral'"
              />
              <SettingsStatusChip
                v-if="connector.insecure_endpoint"
                icon="i-lucide-shield-alert"
                :label="t('MCP_CONNECTORS.LIST.INSECURE')"
                tone="info"
                :data-testid="`mcp-connector-insecure-${connector.id}`"
              />
            </div>
          </div>

          <div class="flex items-center gap-2 ltr:ml-auto rtl:mr-auto">
            <Switch
              :model-value="connector.enabled"
              :data-testid="`mcp-connector-toggle-${connector.id}`"
              @update:model-value="onToggleEnabled(connector)"
            />
            <Button
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-plug-zap"
              :is-loading="testingId === connector.id"
              :aria-label="t('MCP_CONNECTORS.LIST.TEST')"
              :data-testid="`mcp-connector-test-${connector.id}`"
              @click="onTestRow(connector)"
            />
            <Button
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-pencil"
              :aria-label="t('MCP_CONNECTORS.LIST.EDIT')"
              :data-testid="`mcp-connector-edit-${connector.id}`"
              @click="openDialog(connector)"
            />
            <Button
              variant="ghost"
              color="ruby"
              size="xs"
              icon="i-lucide-trash-2"
              :aria-label="t('MCP_CONNECTORS.LIST.DELETE')"
              :data-testid="`mcp-connector-delete-${connector.id}`"
              @click="confirmDelete(connector)"
            />
          </div>
        </div>

        <p
          v-if="connector.insecure_endpoint"
          class="mb-0 text-xs text-n-slate-11"
        >
          {{ t('MCP_CONNECTORS.LIST.INSECURE_HINT') }}
        </p>

        <div
          v-if="rowTestResults[connector.id]"
          class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-1"
          :data-testid="`mcp-connector-result-${connector.id}`"
        >
          <span
            class="text-sm font-medium"
            :class="
              rowTestResults[connector.id].ok
                ? 'text-n-teal-11'
                : 'text-n-ruby-11'
            "
          >
            {{ rowTestResults[connector.id].message }}
          </span>
          <span
            v-if="rowTestResults[connector.id].serverInfo"
            class="text-xs text-n-slate-11"
          >
            {{
              t('MCP_CONNECTORS.TEST.SERVER', {
                name: rowTestResults[connector.id].serverInfo.name,
                version: rowTestResults[connector.id].serverInfo.version,
              })
            }}
          </span>
          <ul
            v-if="rowTestResults[connector.id].tools?.length"
            class="flex flex-col gap-1 m-0 list-none"
          >
            <li
              v-for="tool in rowTestResults[connector.id].tools"
              :key="tool.name"
              class="text-xs text-n-slate-11"
            >
              <span class="font-medium text-n-slate-12">{{ tool.name }}</span>
              <span v-if="tool.description"> — {{ tool.description }}</span>
            </li>
          </ul>
        </div>
      </li>
    </ul>
  </SettingsPageShell>

  <McpConnectorDialog
    ref="connectorDialog"
    :is-submitting="isSaving"
    :is-testing="isTestingDialog"
    :test-result="dialogTestResult"
    @submit="onSubmit"
    @test="onTestFromDialog"
  />

  <ConfirmDialog
    v-model:show="showDeleteConfirm"
    :title="t('MCP_CONNECTORS.LIST.DELETE_CONFIRM_TITLE')"
    :description="t('MCP_CONNECTORS.LIST.DELETE_CONFIRM_DESCRIPTION')"
    :description-value="pendingConnector ? pendingConnector.name : ''"
    :confirm-button-label="t('MCP_CONNECTORS.LIST.DELETE_CONFIRM')"
    :cancel-button-label="t('MCP_CONNECTORS.FORM.CANCEL')"
    @confirm="onDelete"
  />
</template>
